
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import {
  Lead, PermissionCode, ProjectsPool, Task, User,
} from '@/types';
import UTILS from '@/lib/UTILS';
import API from '@/lib/API';
import {
  currentUserHasPermission, getLeadCustomValueBoolean,
  getLeadCustomValueDate, getLeadCustomValueText,
  formatEuroNumber, parseEuroInput,
} from '@/helpers';
import DatePickerDialog from '@/components/DatePickerDialog.vue';

moment.locale('it');

@Component({
  components: {
    DatePickerDialog,
  },
})
export default class EditLeadSupplyDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop(Number) readonly projectId!: number;

  dialog = false
  valid = false
  loading = false

  errorMessage = ''

  datePickerDialog = false
  datePickerDate: string | null = null
  datePickerName: string | null = null

  targetDateUpdatedAt: Date | null = null
  targetDateUpdatedBy: string | null = null
  targetReminderUpdatedAt: Date | null = null
  targetReminderUpdatedBy: string | null = null
  targetDateNotesUpdatedAt: Date | null = null
  targetDateNotesUpdatedBy: string | null = null

  orderConfirmationDateUpdatedAt: Date | null = null
  orderConfirmationDateUpdatedBy: string | null = null

  firstPmClientContactDateUpdatedAt: Date | null = null
  firstPmClientContactDateUpdatedBy: string | null = null

  totalContractValueUpdatedAt: Date | null = null
  totalContractValueUpdatedBy: string | null = null

  // Testo del campo importo in formato italiano; update.totalContractValue
  // riceve la forma canonica solo al salvataggio
  totalContractValueInput = ''
  totalContractValueNotesUpdatedAt: Date | null = null
  totalContractValueNotesUpdatedBy: string | null = null

  supply1updatedAt: Date | null = null
  supply2updatedAt: Date | null = null
  supply3updatedAt: Date | null = null
  supply4updatedAt: Date | null = null
  supply5updatedAt: Date | null = null
  supplyNotesUpdatedAt: Date | null = null

  supply1updatedBy: string | null = null
  supply2updatedBy: string | null = null
  supply3updatedBy: string | null = null
  supply4updatedBy: string | null = null
  supply5updatedBy: string | null = null
  supplyNotesUpdatedBy: string | null = null

  powerDetails1: string | null = null
  powerDetails2: string | null = null
  powerDetails3: string | null = null
  powerDetails4: string | null = null
  powerDetails5: string | null = null

  update = {
    targetDate: null,
    targetReminder: false,
    targetDateNotes: '',

    orderConfirmationDate: null,
    firstPmClientContactDate: null,

    totalContractValue: '',
    totalContractValueNotes: '',

    supply1: false,
    supply2: false,
    supply3: false,
    supply4: false,
    supply5: false,

    supply1text: '',
    supply2text: '',
    supply3text: '',
    supply4text: '',
    supply5text: '',

    supplyNotes: '',
  } as {
    targetDate: Date | null;
    targetReminder: boolean;
    targetDateNotes: string;

    orderConfirmationDate: Date | null;
    firstPmClientContactDate: Date | null;

    totalContractValue: string;
    totalContractValueNotes: string;

    supply1: boolean;
    supply2: boolean;
    supply3: boolean;
    supply4: boolean;
    supply5: boolean;

    supply1text: string;
    supply2text: string;
    supply3text: string;
    supply4text: string;
    supply5text: string;

    supplyNotes: string;
  }

  @Watch('value')
  async onValueChange(val: boolean) {
    console.log(`${this.constructor.name}: onValueChange(${val})`); // eslint-disable-line no-console

    if (val === true) {
      // ... resetta l'eventuale errore del salvataggio precedente
      this.errorMessage = '';

      // Se è stato specificato nelle Props l'id di un progetto (Lead)...
      if (this.projectId) {
        // ... prima di proseguire lo seleziona
        await this.$store.dispatch('selectLead', { id: this.projectId });
      }

      if (this.hasPermission('targetDate.show') && this.lead) {
        this.$set(this.update, 'targetDate', getLeadCustomValueDate(this.lead, 'targetDate'));
        this.$set(this.update, 'targetReminder', getLeadCustomValueBoolean(this.lead, 'targetReminder'));
        this.$set(this.update, 'targetDateNotes', getLeadCustomValueText(this.lead, 'targetDateNotes') ?? '');

        this.targetDateUpdatedBy = await this.getUserNameFromCustomValue('targetDateUpdatedBy');
        this.targetReminderUpdatedBy = await this.getUserNameFromCustomValue('targetReminderUpdatedBy');
        this.targetDateNotesUpdatedBy = await this.getUserNameFromCustomValue('targetDateNotesUpdatedBy');
      }

      if (this.hasPermission('orderConfirmationDate.show') && this.lead) {
        this.$set(this.update, 'orderConfirmationDate', getLeadCustomValueDate(this.lead, 'orderConfirmationDate'));

        this.orderConfirmationDateUpdatedBy = await this.getUserNameFromCustomValue('orderConfirmationDateUpdatedBy');
      }

      if (this.hasPermission('firstPmClientContactDate.show') && this.lead) {
        this.$set(this.update, 'firstPmClientContactDate', getLeadCustomValueDate(this.lead, 'firstPmClientContactDate'));

        this.firstPmClientContactDateUpdatedBy = await this.getUserNameFromCustomValue('firstPmClientContactDateUpdatedBy');
      }

      if (this.hasPermission('totalContractValue.show') && this.lead) {
        this.$set(this.update, 'totalContractValue', getLeadCustomValueText(this.lead, 'totalContractValue') ?? '');
        this.totalContractValueInput = formatEuroNumber(this.update.totalContractValue);
        this.$set(this.update, 'totalContractValueNotes', getLeadCustomValueText(this.lead, 'totalContractValueNotes') ?? '');

        this.totalContractValueUpdatedBy = await this.getUserNameFromCustomValue('totalContractValueUpdatedBy');
        this.totalContractValueNotesUpdatedBy = await this.getUserNameFromCustomValue('totalContractValueNotesUpdatedBy');
      }

      if (this.hasPermission('supply.show')) {
        if (this.lead) {
          this.$set(this.update, 'supply1', getLeadCustomValueBoolean(this.lead, 'supply1'));
          this.$set(this.update, 'supply2', getLeadCustomValueBoolean(this.lead, 'supply2'));
          this.$set(this.update, 'supply3', getLeadCustomValueBoolean(this.lead, 'supply3'));
          this.$set(this.update, 'supply4', getLeadCustomValueBoolean(this.lead, 'supply4'));
          this.$set(this.update, 'supply5', getLeadCustomValueBoolean(this.lead, 'supply5'));

          this.$set(this.update, 'supply1text', getLeadCustomValueText(this.lead, 'supply1text') ?? '');
          this.$set(this.update, 'supply2text', getLeadCustomValueText(this.lead, 'supply2text') ?? '');
          this.$set(this.update, 'supply3text', getLeadCustomValueText(this.lead, 'supply3text') ?? '');
          this.$set(this.update, 'supply4text', getLeadCustomValueText(this.lead, 'supply4text') ?? '');
          this.$set(this.update, 'supply5text', getLeadCustomValueText(this.lead, 'supply5text') ?? '');

          this.powerDetails1 = getLeadCustomValueText(this.lead, 'powerDetails1');
          this.powerDetails2 = getLeadCustomValueText(this.lead, 'powerDetails2');
          this.powerDetails3 = getLeadCustomValueText(this.lead, 'powerDetails3');
          this.powerDetails4 = getLeadCustomValueText(this.lead, 'powerDetails4');
          this.powerDetails5 = getLeadCustomValueText(this.lead, 'powerDetails5');

          this.$set(this.update, 'supplyNotes', getLeadCustomValueText(this.lead, 'supplyNotes') ?? '');

          this.targetDateUpdatedAt = getLeadCustomValueDate(this.lead, 'targetDateUpdatedAt');
          this.targetReminderUpdatedAt = getLeadCustomValueDate(this.lead, 'targetReminderUpdatedAt');
          this.targetDateNotesUpdatedAt = getLeadCustomValueDate(this.lead, 'targetDateNotesUpdatedAt');
          this.totalContractValueUpdatedAt = getLeadCustomValueDate(this.lead, 'totalContractValueUpdatedAt');
          this.totalContractValueNotesUpdatedAt = getLeadCustomValueDate(this.lead, 'totalContractValueNotesUpdatedAt');
          this.orderConfirmationDateUpdatedAt = getLeadCustomValueDate(this.lead, 'orderConfirmationDateUpdatedAt');
          this.firstPmClientContactDateUpdatedAt = getLeadCustomValueDate(this.lead, 'firstPmClientContactDateUpdatedAt');
          this.supply1updatedAt = getLeadCustomValueDate(this.lead, 'supply1updatedAt');
          this.supply2updatedAt = getLeadCustomValueDate(this.lead, 'supply2updatedAt');
          this.supply3updatedAt = getLeadCustomValueDate(this.lead, 'supply3updatedAt');
          this.supply4updatedAt = getLeadCustomValueDate(this.lead, 'supply4updatedAt');
          this.supply5updatedAt = getLeadCustomValueDate(this.lead, 'supply5updatedAt');
          this.supplyNotesUpdatedAt = getLeadCustomValueDate(this.lead, 'supplyNotesUpdatedAt');

          this.supply1updatedBy = await this.getUserNameFromCustomValue('supply1updatedBy');
          this.supply2updatedBy = await this.getUserNameFromCustomValue('supply2updatedBy');
          this.supply3updatedBy = await this.getUserNameFromCustomValue('supply3updatedBy');
          this.supply4updatedBy = await this.getUserNameFromCustomValue('supply4updatedBy');
          this.supply5updatedBy = await this.getUserNameFromCustomValue('supply5updatedBy');
          this.supplyNotesUpdatedBy = await this.getUserNameFromCustomValue('supplyNotesUpdatedBy');
        } else if (this.currentLead) {
          // Fallback: nessun accesso al padre, carica i dati supply via API dal backend
          const result = await API.getLeadSupply(this.currentLead.id);
          if (!(result instanceof Error)) {
            this.$set(this.update, 'supply1', !!result.supply1);
            this.$set(this.update, 'supply2', !!result.supply2);
            this.$set(this.update, 'supply3', !!result.supply3);
            this.$set(this.update, 'supply4', !!result.supply4);
            this.$set(this.update, 'supply5', !!result.supply5);

            this.$set(this.update, 'supply1text', result.supply1text || '');
            this.$set(this.update, 'supply2text', result.supply2text || '');
            this.$set(this.update, 'supply3text', result.supply3text || '');
            this.$set(this.update, 'supply4text', result.supply4text || '');
            this.$set(this.update, 'supply5text', result.supply5text || '');

            this.$set(this.update, 'supplyNotes', result.supplyNotes || '');

            // Aggiorna i timestamp dalle informazioni API
            this.supply1updatedAt = result.supply1updatedAt || null;
            this.supply2updatedAt = result.supply2updatedAt || null;
            this.supply3updatedAt = result.supply3updatedAt || null;
            this.supply4updatedAt = result.supply4updatedAt || null;
            this.supply5updatedAt = result.supply5updatedAt || null;
            this.supplyNotesUpdatedAt = result.supplyNotesUpdatedAt || null;

            // Converti gli ID utenti in nomi utenti usando l'API
            this.supply1updatedBy = result.supply1updatedBy ? await API.getUserName(result.supply1updatedBy) : null;
            this.supply2updatedBy = result.supply2updatedBy ? await API.getUserName(result.supply2updatedBy) : null;
            this.supply3updatedBy = result.supply3updatedBy ? await API.getUserName(result.supply3updatedBy) : null;
            this.supply4updatedBy = result.supply4updatedBy ? await API.getUserName(result.supply4updatedBy) : null;
            this.supply5updatedBy = result.supply5updatedBy ? await API.getUserName(result.supply5updatedBy) : null;
            this.supplyNotesUpdatedBy = result.supplyNotesUpdatedBy ? await API.getUserName(result.supplyNotesUpdatedBy) : null;

            // Power details: recupera dalla lead corrente (figlia)
            this.powerDetails1 = getLeadCustomValueText(this.currentLead, 'powerDetails1');
            this.powerDetails2 = getLeadCustomValueText(this.currentLead, 'powerDetails2');
            this.powerDetails3 = getLeadCustomValueText(this.currentLead, 'powerDetails3');
            this.powerDetails4 = getLeadCustomValueText(this.currentLead, 'powerDetails4');
            this.powerDetails5 = getLeadCustomValueText(this.currentLead, 'powerDetails5');
          }
        }
      }
    }

    this.dialog = val;

    if (val === true) {
      // Primo resize: subito dopo che Vue aggiorna il DOM con i valori precaricati
      this.$nextTick(() => {
        window.dispatchEvent(new Event('resize'));
      });
      // Secondo resize: dopo la transizione di apertura del v-dialog (~225ms)
      // I v-textarea auto-grow non ricalcolano l'altezza durante l'animazione
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    console.log(`${this.constructor.name}: onDialogChange(${val})`); // eslint-disable-line no-console

    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  @Watch('supply1')
  onSupply1Change() {
    const form = this.getForm();
    if (form) form.validate();
  }

  @Watch('supply2')
  onSupply2Change() {
    const form = this.getForm();
    if (form) form.validate();
  }

  @Watch('supply3')
  onSupply3Change() {
    const form = this.getForm();
    if (form) form.validate();
  }

  @Watch('supply4')
  onSupply4Change() {
    const form = this.getForm();
    if (form) form.validate();
  }

  @Watch('supply5')
  onSupply5Change() {
    const form = this.getForm();
    if (form) form.validate();
  }

  @Watch('targetReminder')
  onTargetReminderChange() {
    const form = this.getForm();
    if (form) form.validate();
  }

  get supply1(): boolean { return this.update.supply1; }
  get supply2(): boolean { return this.update.supply2; }
  get supply3(): boolean { return this.update.supply3; }
  get supply4(): boolean { return this.update.supply4; }
  get supply5(): boolean { return this.update.supply5; }

  get targetReminder(): boolean { return this.update.targetReminder; }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get currentLead() {
    return this.$store.getters.currentLead;
  }

  get lead() {
    return this.isFather
      ? this.currentLead
      : this.father;
  }

  get father(): Lead | null {
    const firstTask: Task = this.currentLead.Tasks[0];
    const { fatherLeadId } = firstTask;

    return fatherLeadId && fatherLeadId in this.pool
      ? this.pool[fatherLeadId]
      : null;
  }

  get isChild(): boolean {
    return ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead?.type);
  }

  get isFather(): boolean {
    return !this.isChild;
  }

  getForm(): (Vue & { validate: () => boolean; reset: () => boolean }) | undefined {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  get title(): string {
    const hasTargetDate = this.hasPermission('targetDate.show');
    const hasOrderConfirmation = this.hasPermission('orderConfirmationDate.show');
    const hasFirstPmClientContact = this.hasPermission('firstPmClientContactDate.show');
    const hasContractValue = this.hasPermission('totalContractValue.show');
    const hasSupply = this.hasPermission('supply.show');

    const parts: string[] = [];

    if (hasOrderConfirmation) parts.push('data conferma d\'ordine');
    if (hasTargetDate) parts.push('data obiettivo');
    if (hasFirstPmClientContact) parts.push('data 1° contatto PM-Cliente');
    if (hasContractValue) parts.push('totale contrattuale');
    if (hasSupply) parts.push('oggetto fornitura');

    if (parts.length === 0) return 'Modifica dati';
    if (parts.length === 1) return `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)}`;
    if (parts.length === 2) return `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} e ${parts[1].charAt(0).toUpperCase() + parts[1].slice(1)}`;

    const lastPart = parts.pop();
    // eslint-disable-next-line prefer-template
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ') + ' e ' + lastPart;
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  mDate(date: Date) {
    return moment(date).format('L');
  }

  onClickDate() {
    console.log(`${this.constructor.name}: onClickDate()`); // eslint-disable-line no-console

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = 'targetDate';

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this.update.targetDate
      ? moment(this.update.targetDate).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  async onClearDate() {
    this.$set(this.update, 'targetDate', null);
  }

  onClickOrderConfirmationDate() {
    console.log(`${this.constructor.name}: onClickOrderConfirmationDate()`); // eslint-disable-line no-console

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = 'orderConfirmationDate';

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this.update.orderConfirmationDate
      ? moment(this.update.orderConfirmationDate).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  async onClearOrderConfirmationDate() {
    this.$set(this.update, 'orderConfirmationDate', null);
  }

  onClickFirstPmClientContactDate() {
    console.log(`${this.constructor.name}: onClickFirstPmClientContactDate()`); // eslint-disable-line no-console

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = 'firstPmClientContactDate';

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this.update.firstPmClientContactDate
      ? moment(this.update.firstPmClientContactDate).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  async onClearFirstPmClientContactDate() {
    this.$set(this.update, 'firstPmClientContactDate', null);
  }

  onSelectDate(date: string | null) {
    console.log(`${this.constructor.name}: onSelectDate() date =`, date); // eslint-disable-line no-console

    // Chiudo il dialog per la selezione della data
    this.datePickerDialog = false;

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Imposta la data nel campo appropriato
    if (this.datePickerName === 'targetDate') {
      this.$set(this.update, 'targetDate', selectedDate);
    } else if (this.datePickerName === 'orderConfirmationDate') {
      this.$set(this.update, 'orderConfirmationDate', selectedDate);
    } else if (this.datePickerName === 'firstPmClientContactDate') {
      this.$set(this.update, 'firstPmClientContactDate', selectedDate);
    }
  }

  updatedAtLong(updatedAt: Date | null, updatedBy: string | null) {
    return updatedAt && updatedBy
      ? `Ultima modifica: ${moment(updatedAt).format('L')} ore ${moment(updatedAt).format('LT')} `
        + `(${updatedBy ?? 'Sconosciuto'})`
      : '';
  }

  parseEuroInput(text: string | null | undefined): string | null {
    return parseEuroInput(text);
  }

  async getUserNameFromCustomValue(name: string): Promise<string | null> {
    if (!this.lead) return null;
    const userId = UTILS.getCustomValueUserIdByName(this.lead, name) ?? null;
    const userName = userId ? await API.getUserName(userId) : null;
    return userName;
  }

  supplyLineClass(supply: string): string {
    return this.isSupplyLineSelected(supply) ? 'selected' : '';
  }

  isSupplyLineSelected(supply: string): boolean {
    switch (supply) {
      case 'supply1':
        return this.update.supply1 === true && !!this.update.supply1text
          && this.update.supply2 !== true
          && this.update.supply3 !== true
          && this.update.supply4 !== true
          && this.update.supply5 !== true;

      case 'supply2':
        return this.update.supply2 === true && !!this.update.supply2text
          && this.update.supply3 !== true
          && this.update.supply4 !== true
          && this.update.supply5 !== true;

      case 'supply3':
        return this.update.supply3 === true && !!this.update.supply3text
          && this.update.supply4 !== true
          && this.update.supply5 !== true;

      case 'supply4':
        return this.update.supply4 === true && !!this.update.supply4text
          && this.update.supply5 !== true;

      case 'supply5':
        return this.update.supply5 === true && !!this.update.supply5text;

      default:
        break;
    }

    return false;
  }

  supplyLineIcon(supply: string): string | null {
    if ('supply' in this.update && !this.update[supply]) return 'mdi-lock';

    return this.isSupplyLineSelected(supply) ? 'mdi-check-decagram' : null;
  }

  onCancel() {
    console.log(`${this.constructor.name}: onCancel()`); // eslint-disable-line no-console

    this.dialog = false;
  }

  async onUpdate() {
    console.log(`${this.constructor.name}: onUpdate()`); // eslint-disable-line no-console

    const form = this.getForm();

    if (form?.validate()) {
      this.loading = true;
      this.errorMessage = '';

      const update = {} as { [key: string]: unknown };

      // Include nell'aggiornamento soltanto i dati che l'utente
      // può modificare.

      if (this.hasPermission('targetDate.edit')) {
        update.targetDate = this.update.targetDate;
        update.targetReminder = this.update.targetReminder;
        update.targetDateNotes = this.update.targetDateNotes;
      }

      if (this.hasPermission('orderConfirmationDate.edit')) {
        update.orderConfirmationDate = this.update.orderConfirmationDate;
      }

      if (this.hasPermission('firstPmClientContactDate.edit')) {
        update.firstPmClientContactDate = this.update.firstPmClientContactDate;
      }

      if (this.hasPermission('totalContractValue.edit')) {
        // Il form e' gia' validato: parseEuroInput non puo' dare null qui
        update.totalContractValue = parseEuroInput(this.totalContractValueInput) ?? '';
        update.totalContractValueNotes = this.update.totalContractValueNotes;
      }

      if (this.hasPermission('supply.edit')) {
        update.supply1 = this.update.supply1;
        update.supply2 = this.update.supply2;
        update.supply3 = this.update.supply3;
        update.supply4 = this.update.supply4;
        update.supply5 = this.update.supply5;

        update.supply1text = this.update.supply1text;
        update.supply2text = this.update.supply2text;
        update.supply3text = this.update.supply3text;
        update.supply4text = this.update.supply4text;
        update.supply5text = this.update.supply5text;

        update.supplyNotes = this.update.supplyNotes;
      }

      // Usa direttamente l'API per aggiornare i dati supply
      // Questo gestisce automaticamente il caso delle lead figlie
      const updateResult = await API.updateLeadSupply(this.currentLead.id, update);

      // API.execute non lancia mai un'eccezione: in caso di errore (es. 400 per
      // un totale contrattuale non canonico) restituisce l'oggetto Error. Il
      // dialog resta aperto e mostra il messaggio, altrimenti sembrerebbe che
      // il salvataggio sia riuscito.
      if (updateResult instanceof Error) {
        this.errorMessage = (updateResult as any).response?.data?.message || 'Salvataggio non riuscito, riprova.';
        this.loading = false;
        return;
      }

      // Ricarica i dati supply per aggiornare l'interfaccia
      if (this.hasPermission('supply.show')) {
        if (this.lead) {
          // Se abbiamo accesso al padre, ricarica la lead
          await this.$store.dispatch('refreshLead');
        } else if (this.currentLead) {
          // Se non abbiamo accesso al padre, ricarica i dati supply via API
          const result = await API.getLeadSupply(this.currentLead.id);
          if (!(result instanceof Error)) {
            this.$set(this.update, 'supply1', !!result.supply1);
            this.$set(this.update, 'supply2', !!result.supply2);
            this.$set(this.update, 'supply3', !!result.supply3);
            this.$set(this.update, 'supply4', !!result.supply4);
            this.$set(this.update, 'supply5', !!result.supply5);

            this.$set(this.update, 'supply1text', result.supply1text || '');
            this.$set(this.update, 'supply2text', result.supply2text || '');
            this.$set(this.update, 'supply3text', result.supply3text || '');
            this.$set(this.update, 'supply4text', result.supply4text || '');
            this.$set(this.update, 'supply5text', result.supply5text || '');

            this.$set(this.update, 'supplyNotes', result.supplyNotes || '');

            // Aggiorna anche i timestamp dopo l'aggiornamento
            this.supply1updatedAt = result.supply1updatedAt || null;
            this.supply2updatedAt = result.supply2updatedAt || null;
            this.supply3updatedAt = result.supply3updatedAt || null;
            this.supply4updatedAt = result.supply4updatedAt || null;
            this.supply5updatedAt = result.supply5updatedAt || null;
            this.supplyNotesUpdatedAt = result.supplyNotesUpdatedAt || null;

            // Converti gli ID utenti in nomi utenti dopo l'aggiornamento
            this.supply1updatedBy = result.supply1updatedBy ? await API.getUserName(result.supply1updatedBy) : null;
            this.supply2updatedBy = result.supply2updatedBy ? await API.getUserName(result.supply2updatedBy) : null;
            this.supply3updatedBy = result.supply3updatedBy ? await API.getUserName(result.supply3updatedBy) : null;
            this.supply4updatedBy = result.supply4updatedBy ? await API.getUserName(result.supply4updatedBy) : null;
            this.supply5updatedBy = result.supply5updatedBy ? await API.getUserName(result.supply5updatedBy) : null;
            this.supplyNotesUpdatedBy = result.supplyNotesUpdatedBy ? await API.getUserName(result.supplyNotesUpdatedBy) : null;
          }
        }
      }

      this.dialog = false;
      setTimeout(() => {
        this.loading = false;
      }, 500);
    }
  }
}
